import * as React from 'react';
import type { CSSProperties } from 'react';
import { cx } from '../utils';
import { tintVar, type StyledProps, type TintName, type WithChildren } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

/** Elements DisplayNum can render as. Unlike Title (h1–h6) and Eyebrow
 *  (p / span / div), there is no single "semantic numeric display" HTML
 *  element. v5.0 keeps `<div>` as the default to avoid an arbitrary
 *  semantic-element choice — adopters who want to commit to a specific
 *  element pick from this list explicitly.
 *
 *  - `output` — form-associated calculated value (best when the number
 *    is computed from inputs the user can change; pair with `for=…`)
 *  - `data`   — machine-readable + display value pair (best when the
 *    number is a structured value, e.g., a product ID); typically with
 *    a `value` attribute, which adopters add via `...rest` if needed
 *  - `span`   — neutral inline (good for inline metric callouts)
 *  - `p`      — block paragraph (good for standalone metrics)
 *  - `div`    — default; neutral block, no implied semantics */
export type DisplayNumAs = 'output' | 'data' | 'span' | 'p' | 'div';

/* DisplayNumProps stays a stable interface (NOT a discriminated union)
   for backward compatibility — adopter wrappers using
   `interface Props extends DisplayNumProps` continue to compile.
   Rest props are typed as React.HTMLAttributes<HTMLElement> which
   covers ~99% of real-world use (aria-*, id, data-*, role,
   onClick, etc.).
   Known limitation: element-specific attributes that DON'T exist on
   HTMLAttributes (e.g., `value` on <data>, `htmlFor`/`form`/`name`
   on <output>) require a cast at the call site, for example
   `(<DisplayNum as="data" {...{ value: '42' } as any}>)`. Implementing
   a fully type-safe polymorphic surface is non-trivial; an earlier
   iteration via a discriminated union broke the `interface extends`
   pattern that adopter wrappers rely on. The runtime spread DOES
   forward these attributes correctly regardless of the type signature
   — see tests `typed: as="data" accepts value` and
   `typed: as="output" accepts htmlFor and name`. */
export interface DisplayNumProps
  extends
    StyledProps,
    WithChildren,
    UnsafeStyleProps,
    Omit<React.HTMLAttributes<HTMLElement>, 'className' | 'style' | 'children'> {
  /** Pixel font-size override (no unit; px is implied). ADR-017
   *  §"unsafe_*" escape hatch — bypasses the typography ramp (--type-*
   *  tokens) and emits an inline font-size declaration. Use sparingly;
   *  prefer the level-driven classes on `<Title>` for most cases.
   *  Audit #12 (2026-05-16): renamed from `size` to `unsafe_size` so
   *  the API surface signals the bypass clearly. */
  unsafe_size?: number;
  tone?: TintName;
  /** Override the rendered element. Defaults to `'div'` — see source
   *  for the rationale. New in v5.0. */
  as?: DisplayNumAs;
}

/* forwardRef so adopters can attach refs for measurement (e.g., resize
   observer on metric cards) or focus management (output elements
   can receive focus in some flows). Ref type is HTMLElement since
   `as` covers div/span/p/output/data uniformly. */
export const DisplayNum = React.forwardRef<HTMLElement, DisplayNumProps>(function DisplayNum(
  { children, unsafe_size, tone, as = 'div', className, unsafe_style, ...rest },
  ref,
) {
  const inline: CSSProperties = {};
  if (unsafe_size != null) inline.fontSize = `${unsafe_size}px`;
  /* tintVar resolves `tone` → `var(--color-{tint}-700)` against the
     v5 semantic-token palette. Returns undefined for unknown tints so
     a typo or malformed consumer input can't inject arbitrary CSS.
     Audit #9 fix (2026-05-16): replaces the prior `var(--${tone})`
     interpolation which referenced v4 aliases never defined as CSS
     vars in v5's tokens.css. */
  const toneColor = tintVar(tone);
  if (toneColor) inline.color = toneColor;
  const Element = as as React.ElementType;
  /* Forward extra HTMLAttributes (e.g., aria-live, id, data-*, and
     element-specific attrs like `htmlFor`/`form` for <output> or
     `value` for <data>). React's JSX type narrows by intrinsic
     element name; here we accept the union via HTMLElement and
     let consumers pass element-appropriate attributes. */
  return (
    <Element
      ref={ref}
      {...rest}
      className={cx('j-display-num', className)}
      style={{ ...inline, ...unsafe_style }}
    >
      {children}
    </Element>
  );
});
